import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureGate, FeatureType } from '../../context/FeatureContext';
import NotificationBubble from '../NotificationBubble';
import SidebarItem from '../Sidebar/SidebarItem';
import SpeechBubblePortal from '../SpeechBubblePortal';

const STORAGE_KEY = 'entity-association-dismissed';
const DISMISS_DURATION = 1000 * 60 * 60 * 24; // 24 hours

interface EntityAssociationSidebarItemProps {
  expandDueToChildren: boolean;
  onHoverIntent: (hasChildren: boolean) => void;
  count?: number;
  investorIds?: string[];
  disabled?: boolean;
}

const EntityAssociationSidebarItem: React.FC<EntityAssociationSidebarItemProps> = ({
  expandDueToChildren,
  onHoverIntent,
  count = 0,
  investorIds,
  disabled = false,
}) => {
  const location = useLocation();
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [refReady, setRefReady] = useState(false);
  const sidebarItemRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) {
      hamburgerRef.current = document.querySelector('.sidebar-hamburger');
    }
  }, [isMobile]);

  const handleDismiss = () => {
    setShowSpeechBubble(false);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ investorIds: investorIds!, timestamp: Date.now() }),
    );
  };

  // Mark ref as ready once it's mounted
  useLayoutEffect(() => {
    if (sidebarItemRef.current) {
      setRefReady(true);
    }
  }, []);

  // Show speech bubble when count > 0 and ref is available
  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) {
      try {
        const { investorIds: dismissedIds, timestamp } = JSON.parse(dismissed);

        // Only stay dismissed if every currently-pending investor was
        // already accounted for at dismiss time — a newly-eligible investor
        // (not in dismissedIds) should re-trigger the notification.
        if (
          Array.isArray(dismissedIds) &&
          (investorIds ?? []).every((id) => dismissedIds.includes(id)) &&
          timestamp > Date.now() - DISMISS_DURATION
        ) {
          setShowSpeechBubble(false);
          return;
        }
      } catch {
        // Malformed/legacy value — fall through and treat as not dismissed.
      }
    }

    if (count > 0 && refReady) {
      const timer = setTimeout(() => {
        setShowSpeechBubble(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowSpeechBubble(false);
    }
  }, [count, refReady]);

  return (
    <FeatureGate feature={FeatureType.FeatureInvestmentEntities}>
      <Box sx={{ display: 'inline-block', width: '100%' }}>
        <SidebarItem
          ref={sidebarItemRef}
          to="/entity-association"
          label="Entities"
          icon="Entity"
          selected={location.pathname === '/entity-association'}
          expanded={expandDueToChildren}
          onHoverIntent={onHoverIntent}
          disabled={disabled}
          notificationComponent={
            <NotificationBubble show={count > 0} count={count} onDismiss={() => {}} />
          }
        />
      </Box>

      <SpeechBubblePortal
        dismissable={true}
        tone="tone4"
        show={showSpeechBubble && !!(isMobile ? hamburgerRef.current : sidebarItemRef.current)}
        message={`${count} invesment${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} to be associated with an entity!`}
        targetRef={isMobile ? hamburgerRef : sidebarItemRef}
        onClose={handleDismiss}
      />
    </FeatureGate>
  );
};

export default EntityAssociationSidebarItem;
